import { Box, Button, Card, CardContent, Typography, Chip } from '@mui/material'
import { IconBrandInstagram } from '@tabler/icons-react'
import React, { useMemo } from 'react'
import { openInstagram } from '../helpers/instagram-helper'
import useAppUtilities from '../hooks/useAppUtilities'
import { useAppSelector } from '../store'
import {
  awardInstagramPoints,
  selectEater,
  selectIsLoading,
  fetchEater,
} from '../store/profileSlice'
import { errorLog } from '../utils/log-helper'
import { logEvent } from '../analytics/analytics'

interface InstagramEngagementBonusCardProps {
  /** Called after a successful award so the parent can refresh the displayed points total in place. */
  onPointsAwarded?: () => void | Promise<void>
}

const InstagramEngagementBonusCard: React.FC<InstagramEngagementBonusCardProps> = ({
  onPointsAwarded,
}) => {
  const { dispatch, theme } = useAppUtilities()
  const eater = useAppSelector(selectEater)
  const isLoading = useAppSelector(selectIsLoading)
  
  // Get Instagram earn way from backend earnWays
  const instagramEarnWay = useMemo(() => {
    return eater?.earnWays?.find((way) => way.key === 'instagram_follow')
  }, [eater?.earnWays])
  
  // Use instagramBonusClaimed from backend, fallback to hasClaimedInstagramBonus for backward compatibility
  const instagramBonusClaimed = eater?.instagramBonusClaimed === true || eater?.hasClaimedInstagramBonus === true
  
  // Get points and copy from earnWays, fallback to defaults
  const instagramPoints = instagramEarnWay?.pointsHint ?? 50
  const instagramCtaText = instagramEarnWay?.ctaText ?? 'Engage With Us on Instagram'

  const handleInstagramClick = async (): Promise<void> => {
    if (instagramBonusClaimed) {
      // Already claimed - just open Instagram
      await openInstagram()
      return
    }

    // Not claimed yet - call reward endpoint first
    try {
      const result = await dispatch(awardInstagramPoints())
      
      if (awardInstagramPoints.fulfilled.match(result)) {
        // Refetch eater to get updated instagramBonusClaimed status
        await dispatch(fetchEater())
        // Refresh the points total in place so the earning shows immediately (no navigate-away).
        // The backend now evicts the balance cache on award, so this reads the new total.
        await onPointsAwarded?.()
        logEvent('points_earned', {
          points_amount: instagramPoints,
          reason: 'instagram_engagement',
          screen_name: 'rewards',
        })
        // Open Instagram URL after successful claim
        await openInstagram()
      } else if (awardInstagramPoints.rejected.match(result)) {
        const payload = result.payload as { message?: string; alreadyClaimed?: boolean } | undefined
        if (payload?.alreadyClaimed) {
          // Already claimed error - refetch to update state
          await dispatch(fetchEater())
          // Still open Instagram
          await openInstagram()
        }
        // Error handling is done in the reducer
      }
    } catch (error) {
      errorLog('Error claiming Instagram bonus:', error)
    }
  }

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(16, 24, 40, 0.08)',
        },
      }}
    >
      <CardContent
        sx={{
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCAF45 100%)',
              color: '#FFF',
              flexShrink: 0,
            }}
          >
            <IconBrandInstagram size={20} stroke={2} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                lineHeight: '20px',
                color: theme.palette.grey[900],
                marginBottom: '2px',
              }}
            >
              Engage with us on Instagram
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 400,
                lineHeight: '16px',
                color: theme.palette.grey[600],
              }}
            >
              {instagramBonusClaimed ? 'Visit our Instagram' : `Earn +${instagramPoints} points`}
            </Typography>
          </Box>
          {!instagramBonusClaimed && (
            <Chip
              label={`+${instagramPoints} pts`}
              size="small"
              sx={{
                backgroundColor: theme.palette.success[50],
                color: theme.palette.success[700],
                fontWeight: 600,
                fontSize: '11px',
                height: '22px',
                flexShrink: 0,
              }}
            />
          )}
          {instagramBonusClaimed && (
            <Chip
              label="Bonus claimed"
              size="small"
              sx={{
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '11px',
                height: '22px',
                flexShrink: 0,
              }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            void handleInstagramClick()
          }}
          disabled={isLoading}
          startIcon={<IconBrandInstagram size={16} stroke={2} />}
          sx={{
            backgroundColor: '#E4405F',
            color: '#FFF',
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#C13584',
            },
            '&:disabled': {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.grey[500],
            },
          }}
        >
          {isLoading
            ? 'Processing...'
            : instagramBonusClaimed
              ? 'Visit our Instagram'
              : instagramCtaText}
        </Button>
      </CardContent>
    </Card>
  )
}

export default InstagramEngagementBonusCard
